import { BybProgressBar, BybProgressBarTarget } from "@Components/common/byb-ds/cards/styled";
import ScanRateScoreMaxedIndicator from "@Components/common/scan-rate/scan-rate-score-maxed-indicator";
import MetricTypeIcon from "@Containers/mobile/helpers/get-metric-type-icon";
import { AppContext } from "@Context/common/general/context";
import MetricFilterTypes from "@Enums/MetricFilterTypes";
import { getPerformanceLabel, getPerformanceShade } from "@Helpers/feedback";
import useI18n from "@i18n";
import { Box, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { isScanRateScoreMaxed } from "@Shared/utilities/scanScoreUtilities";
import { isNumber } from "lodash-es";
import { useContext } from "react";
import { getDisplayValue, getProgressPercentage, normalizeMetricValue } from "./byb-metric-card.utils";

interface IBybMetricCard {
  metricType?: MetricFilterTypes;
  score?: number;
  target?: number;
  compact?: boolean;
}

const BybMetricCard = ({ metricType, score, target, compact = false }: IBybMetricCard) => {
  const { i18n } = useI18n();
  const { state } = useContext(AppContext);
  const theme = useTheme();

  const creationDate = state.user.creationDate ? new Date(state.user.creationDate).getTime() : null;
  const normalizedScore = metricType ? normalizeMetricValue(metricType, score) : undefined;
  const progressValue = metricType ? getProgressPercentage(metricType, score) : undefined;
  const targetPosition = metricType ? getProgressPercentage(metricType, target) : undefined;
  const displayValue = metricType ? getDisplayValue(metricType, score) : null;
  const showScanRateMaxed = metricType === MetricFilterTypes.SCAN_FREQUENCY && isNumber(score) && isScanRateScoreMaxed(score);

  const renderHeader = () => (
    <Stack direction="row" alignItems={"center"} justifyContent={"space-between"} width={"100%"} spacing={1.5}>
      {metricType ? (
        <>
          <Stack direction="row" alignItems="center" spacing={1.5} minWidth={0}>
            <MetricTypeIcon metricType={metricType} size={compact ? 5 : 6} />
            {!compact && <Typography variant="textLg500">{i18n(`metric.${metricType}.name`)}</Typography>}
          </Stack>
          {!compact && showScanRateMaxed && <ScanRateScoreMaxedIndicator />}
        </>
      ) : (
        <Skeleton variant="rounded" height={45} width={45} sx={{ borderRadius: 3 }}></Skeleton>
      )}
    </Stack>
  );

  const renderMetricValue = () => (
    <>
      {metricType ? (
        <Typography variant={compact ? "text1xl600" : "text2xl600"}>
          {isNumber(normalizedScore) ? displayValue : "--"} {i18n(`metric.${metricType}.short`)}
        </Typography>
      ) : (
        <Skeleton variant="rounded" height={27.5} width={50}></Skeleton>
      )}
    </>
  );

  const renderSubtext = () => {
    if (!metricType) {
      return <Skeleton variant="rounded" height={20} width={100} />;
    }

    if (compact) {
      return (
        <Typography variant="textS500" color={theme.palette.text.secondary}>
          {i18n(`metric.${metricType}.name`)}
        </Typography>
      );
    }

    if (!isNumber(target)) {
      return null;
    }

    return (
      <Typography variant="textBase500" color={theme.palette.dsNeutral.v300}>
        {i18n("challenges.reward.target", {
          target: getDisplayValue(metricType, target),
          metric: i18n(`metric.${metricType}.short`),
        })}
      </Typography>
    );
  };

  const renderPerformanceLabel = () => {
    if (!metricType) {
      return <Skeleton variant="rounded" height={36} width={80} sx={{ borderRadius: 2 }} />;
    }

    if (!isNumber(normalizedScore)) {
      return null;
    }

    const color = getPerformanceShade(metricType, normalizedScore, theme, creationDate).v300;

    return (
      <Stack spacing={0.5} bgcolor={`${color}20`} padding={1} paddingX={2} borderRadius={2}>
        <Typography variant="textBase500" color={color}>
          {i18n(`performance.label.${getPerformanceLabel(metricType, normalizedScore, creationDate)}`)}
        </Typography>
      </Stack>
    );
  };

  const renderProgressBarWithTarget = () => {
    if (!metricType) {
      return <Skeleton variant="rounded" width={"100%"} height={10} />;
    }

    if (!isNumber(progressValue)) {
      return null;
    }

    return (
      <Box sx={{ position: "relative", width: "100%" }}>
        <BybProgressBar variant="determinate" value={progressValue} metricType={metricType} />
        {isNumber(targetPosition) && <BybProgressBarTarget targetPosition={targetPosition} metricType={metricType} />}
      </Box>
    );
  };

  return (
    <Stack borderRadius={3} padding={2} spacing={3} border={`2px solid ${theme.palette.dsSurface.v300}`} position="relative">
      {compact && showScanRateMaxed && (
        <Box sx={{ position: "absolute", top: theme.spacing(2), right: theme.spacing(2), zIndex: 1 }}>
          <ScanRateScoreMaxedIndicator />
        </Box>
      )}
      {renderHeader()}

      <Stack spacing={0}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack spacing={0.5}>
            {renderMetricValue()}
            {renderSubtext()}
          </Stack>
          {!compact && renderPerformanceLabel()}
        </Stack>

        {compact && <Stack paddingTop={1}>{renderProgressBarWithTarget()}</Stack>}
      </Stack>
    </Stack>
  );
};

export default BybMetricCard;
